import React, { useEffect, useId, useRef } from 'react';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import '../../styles/photoswipe-custom.css';
import { LiveMediaItem, UPLOADS_BASE } from '../../services/liveService';

interface Props {
  items: LiveMediaItem[];
  alt?: string;
}

const srcOf = (m: LiveMediaItem) =>
  (/^https?:\/\//.test(m.url) ? m.url : `${UPLOADS_BASE}${m.url}`);

const LivePhotoGrid: React.FC<Props> = ({ items, alt = '' }) => {
  const id = useId().replace(/[:]/g, '');
  const containerId = `live-grid-${id}`;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!items || items.length === 0) return;
    const lightbox = new PhotoSwipeLightbox({
      gallery: `#${containerId}`,
      children: 'a',
      pswpModule: () => import('photoswipe'),
      bgOpacity: 0.95,
      spacing: 0.1,
      allowPanToNext: true,
      loop: true,
      zoom: true,
      maxZoomLevel: 4,
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 1.5,
      pinchToClose: true,
      closeOnVerticalDrag: true,
      escKey: true,
      arrowKeys: true,
      showHideAnimationType: 'fade',
      showAnimationDuration: 333,
      hideAnimationDuration: 333,
    });

    // Slideshow: solo se ci sono piu\` foto. 3 secondi per slide; loop attivo.
    if (items.length > 1) {
      lightbox.on('uiRegister', () => {
        const pswp = lightbox.pswp;
        if (!pswp) return;
        let timer: ReturnType<typeof setInterval> | null = null;
        let playing = false;

        const PLAY_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4"/></svg>';
        const PAUSE_SVG = '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>';

        const stop = (el?: HTMLElement) => {
          if (timer) { clearInterval(timer); timer = null; }
          playing = false;
          if (el) el.innerHTML = PLAY_SVG;
        };

        pswp.ui!.registerElement({
          name: 'slideshow',
          order: 7,
          isButton: true,
          tagName: 'button',
          html: PLAY_SVG,
          onClick: (_e, el) => {
            if (playing) {
              stop(el);
            } else {
              playing = true;
              el.innerHTML = PAUSE_SVG;
              timer = setInterval(() => {
                if (pswp) pswp.next();
              }, 3000);
            }
          }
        });

        pswp.on('close', () => { if (timer) clearInterval(timer); });
      });
    }

    lightbox.init();
    return () => lightbox.destroy();
  }, [containerId, items]);

  // Quando l'immagine si carica, applichiamo width/height naturali sull'anchor
  // per PhotoSwipe (cosi\` il lightbox a schermo intero mantiene l'aspect-ratio
  // originale — senza queste dimensioni PhotoSwipe usa 0/0 e stira l'immagine).
  const measureOnLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const a = img.closest('a');
    if (a && img.naturalWidth && img.naturalHeight) {
      a.setAttribute('data-pswp-width', String(img.naturalWidth));
      a.setAttribute('data-pswp-height', String(img.naturalHeight));
    }
  };

  // Precarica anche le immagini "hidden" (oltre la 4a) per misurarne le dimensioni
  // prima che l'utente le veda nel lightbox.
  const measureHidden = (url: string, anchor: HTMLAnchorElement | null) => {
    if (!anchor) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        anchor.setAttribute('data-pswp-width', String(img.naturalWidth));
        anchor.setAttribute('data-pswp-height', String(img.naturalHeight));
      }
    };
    img.src = url;
  };

  if (!items || items.length === 0) return null;

  const n = items.length;
  if (n === 1) {
    const m = items[0];
    return (
      <div id={containerId} ref={ref}>
        <a
          href={srcOf(m)}
          data-pswp-width={m.width || 0}
          data-pswp-height={m.height || 0}
          target="_blank"
          rel="noreferrer"
        >
          <img src={srcOf(m)} alt={alt} loading="lazy" onLoad={measureOnLoad} />
        </a>
      </div>
    );
  }

  const visible = items.slice(0, Math.min(n, 4));
  const overflow = Math.max(0, n - 4);

  return (
    <div id={containerId} ref={ref} className="live-photo-grid" data-count={Math.min(n, 4)}>
      {visible.map((m, i) => {
        const isFirstOfThree = n === 3 && i === 0;
        const isLastVisible = i === visible.length - 1 && overflow > 0;
        return (
          <a
            key={m.url + i}
            href={srcOf(m)}
            data-pswp-width={m.width || 0}
            data-pswp-height={m.height || 0}
            target="_blank"
            rel="noreferrer"
            className={`lpg-item${isFirstOfThree ? ' lpg-tall' : ''}`}
          >
            <img src={srcOf(m)} alt={alt} loading="lazy" onLoad={measureOnLoad} />
            {isLastVisible && <div className="lpg-overflow">+{overflow}</div>}
          </a>
        );
      })}
      {overflow > 0 && items.slice(4).map((m, i) => (
        <a
          key={'hidden-' + m.url + i}
          href={srcOf(m)}
          data-pswp-width={m.width || 0}
          data-pswp-height={m.height || 0}
          style={{ display: 'none' }}
          aria-hidden
          ref={(el) => measureHidden(srcOf(m), el)}
        >
          {alt}
        </a>
      ))}
    </div>
  );
};

export default LivePhotoGrid;
